import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { BaseApiService } from "../../../core/api/base-api.service";
import { API_CONFIG } from "../../../core/api/api.config";
import {
  CQRSService,
  EventHistorySummary,
  EventHistoryItem,
} from "../../../core/services/cqrs.service";

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  status: "ACTIVE" | "SUSPENDED" | "INACTIVE" | "DELETED";
  plan: "BASIC" | "PRO" | "ENTERPRISE" | "CUSTOM";
  isSeparateDb: boolean;
  maxUsers?: number;
  maxTasks?: number;
  maxStorage?: number;
  billingEmail?: string;
  billingAddress?: any;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionEndsAt?: string;
  dbConnectionString?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface TenantStats {
  totalUsers: number;
  activeUsers: number;
  totalTasks: number;
  completedTasks: number;
  totalProjects: number;
  organizations: number;
  teams: number;
  storageUsed: number;
  limits: {
    maxUsers?: number;
    maxTasks?: number;
    maxStorage?: number;
  };
  usage: {
    userUsagePercent?: number;
    taskUsagePercent?: number;
  };
}

export interface TenantSummary {
  total: number;
  active: number;
  suspended: number;
  inactive: number;
  byPlan: Array<{ plan: string; count: number }>;
}

export interface CreateTenantDto {
  name: string;
  slug: string;
  domain?: string;
  status?: string;
  plan?: string;
  maxUsers?: number;
  maxTasks?: number;
  maxStorage?: number;
  billingEmail?: string;
  settings?: any;
}

export interface UpdateTenantDto {
  name?: string;
  slug?: string;
  domain?: string;
  status?: string;
  plan?: string;
  maxUsers?: number;
  maxTasks?: number;
  maxStorage?: number;
  billingEmail?: string;
  settings?: any;
  subscriptionId?: string;
  subscriptionStatus?: string;
}

export interface PlatformAnalytics {
  overview: {
    total_tenants: number;
    active_tenants: number;
    suspended_tenants: number;
    inactive_tenants: number;
    total_users: number;
    active_users: number;
    total_tasks: number;
    completed_tasks: number;
    total_projects: number;
  };
  growth: {
    last30Days: {
      newTenants: number;
      newUsers: number;
      newTasks: number;
    };
  };
  distribution: {
    byPlan: Array<{ plan: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
  };
}

export interface TenantReport {
  tenant: Tenant;
  period: { start: Date; end: Date };
  statistics: TenantStats;
  trends: {
    userGrowth: Record<string, number>;
    taskTrends: Record<string, { created: number; completed: number }>;
  };
  activity: any[];
}

export interface TenantUsage {
  users: {
    current: number;
    limit?: number;
    usagePercent?: number;
  };
  tasks: {
    current: number;
    limit?: number;
    usagePercent?: number;
  };
  storage: {
    current: number;
    limit?: number;
    usagePercent?: number;
  };
}

export interface TenantBilling {
  id: string;
  name: string;
  plan: string;
  billingEmail?: string;
  billingAddress?: any;
  subscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionEndsAt?: string;
  createdAt: string;
}

export interface ResourceUsage {
  tenants: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    plan: string;
    max_users?: number;
    max_tasks?: number;
    current_users: number;
    current_tasks: number;
    user_usage_percent?: number;
    task_usage_percent?: number;
    user_status: "normal" | "warning" | "critical";
    task_status: "normal" | "warning" | "critical";
  }>;
  summary: {
    total: number;
    atWarning: number;
    atCritical: number;
    healthy: number;
  };
}

export interface SystemHealth {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    inactive: number;
  };
  users: {
    total: number;
    active: number;
  };
  tasks: {
    total: number;
    completed: number;
  };
  projects: {
    total: number;
  };
  activity: {
    last24Hours: {
      newTenants: number;
      newUsers: number;
      newTasks: number;
    };
  };
  timestamp: string;
}

@Injectable({ providedIn: "root" })
export class PlatformApiService extends BaseApiService {
  private readonly cqrsService = inject(CQRSService);

  protected get baseUrl(): string {
    return `${API_CONFIG.getUrl(API_CONFIG.endpoints.gateway)}/platform`;
  }

  // ==================== CQRS COMMANDS (Write Operations) ====================

  /**
   * Create tenant using CQRS command
   */
  createTenantCommand(
    data: CreateTenantDto,
    metadata?: { requestId?: string; correlationId?: string }
  ): Observable<{ success: boolean; message?: string; data: Tenant }> {
    return this.cqrsService
      .executeCommand(
        "CreateTenant",
        data,
        undefined,
        metadata,
        `${this.baseUrl}/tenants`
      )
      .pipe(
        map((response) => ({
          success: response.success,
          message: response.message,
          data: response.data,
        }))
      );
  }

  // ==================== CQRS QUERIES (Read Operations) ====================

  /**
   * Get tenant using CQRS query
   */
  getTenantQuery(
    id: string,
    metadata?: { cacheKey?: string; cacheTTL?: number }
  ): Observable<{ success: boolean; data: Tenant }> {
    return this.cqrsService
      .executeQuery<Tenant>(
        "GetTenant",
        undefined,
        id,
        metadata,
        `${this.baseUrl}/tenants`
      )
      .pipe(
        map((response) => ({
          success: response.success,
          data: response.data!,
        }))
      );
  }

  /**
   * Get complete event history for a tenant
   */
  getTenantEventHistory(
    tenantId: string,
    filters?: {
      fromVersion?: number;
      toVersion?: number;
      fromDate?: Date | string;
      toDate?: Date | string;
      eventType?: string;
      performedBy?: string;
    }
  ): Observable<{
    success: boolean;
    data: EventHistorySummary | EventHistoryItem[];
  }> {
    return this.cqrsService.getEventHistory(tenantId, "Tenant", filters).pipe(
      map((response) => ({
        success: response.success,
        data: response.data!,
      }))
    );
  }

  // ========== TENANTS ==========

  getTenants(params?: {
    status?: string;
    plan?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Observable<{ success: boolean; data: Tenant[]; pagination?: any }> {
    let httpParams = new HttpParams();
    if (params?.status) httpParams = httpParams.set("status", params.status);
    if (params?.plan) httpParams = httpParams.set("plan", params.plan);
    if (params?.search) httpParams = httpParams.set("search", params.search);
    if (params?.page)
      httpParams = httpParams.set("page", params.page.toString());
    if (params?.limit)
      httpParams = httpParams.set("limit", params.limit.toString());
    if (params?.sortBy) httpParams = httpParams.set("sortBy", params.sortBy);
    if (params?.sortOrder)
      httpParams = httpParams.set("sortOrder", params.sortOrder);

    return this.get<{ success: boolean; data: Tenant[]; pagination?: any }>(
      "/tenants",
      { params: httpParams }
    );
  }

  getTenantsSummary(): Observable<{ success: boolean; data: TenantSummary }> {
    return this.get<{ success: boolean; data: TenantSummary }>(
      "/tenants/summary"
    );
  }

  getTenant(id: string): Observable<{ success: boolean; data: Tenant }> {
    return this.get<{ success: boolean; data: Tenant }>(`/tenants/${id}`);
  }

  getTenantStats(
    id: string
  ): Observable<{ success: boolean; data: TenantStats }> {
    return this.get<{ success: boolean; data: TenantStats }>(
      `/tenants/${id}/stats`
    );
  }

  getTenantReport(
    id: string,
    startDate?: string,
    endDate?: string
  ): Observable<{ success: boolean; data: TenantReport }> {
    let httpParams = new HttpParams();
    if (startDate) httpParams = httpParams.set("startDate", startDate);
    if (endDate) httpParams = httpParams.set("endDate", endDate);
    return this.get<{ success: boolean; data: TenantReport }>(
      `/tenants/${id}/report`,
      { params: httpParams }
    );
  }

  getTenantUsers(
    id: string,
    page?: number,
    limit?: number
  ): Observable<{ success: boolean; data: any[]; pagination?: any }> {
    let httpParams = new HttpParams();
    if (page) httpParams = httpParams.set("page", page.toString());
    if (limit) httpParams = httpParams.set("limit", limit.toString());
    return this.get<{ success: boolean; data: any[]; pagination?: any }>(
      `/tenants/${id}/users`,
      { params: httpParams }
    );
  }

  getTenantActivity(
    id: string,
    days?: number,
    page?: number
  ): Observable<{ success: boolean; data: any[]; pagination?: any }> {
    let httpParams = new HttpParams();
    if (days) httpParams = httpParams.set("days", days.toString());
    if (page) httpParams = httpParams.set("page", page.toString());
    return this.get<{ success: boolean; data: any[]; pagination?: any }>(
      `/tenants/${id}/activity`,
      { params: httpParams }
    );
  }

  createTenant(
    dto: CreateTenantDto
  ): Observable<{ success: boolean; data: Tenant }> {
    return this.post<{ success: boolean; data: Tenant }>("/tenants", dto);
  }

  updateTenant(
    id: string,
    dto: UpdateTenantDto
  ): Observable<{ success: boolean; data: Tenant }> {
    return this.patch<{ success: boolean; data: Tenant }>(
      `/tenants/${id}`,
      dto
    );
  }

  suspendTenant(
    id: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.put<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/suspend`,
      {}
    );
  }

  activateTenant(
    id: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.put<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/activate`,
      {}
    );
  }

  restoreTenant(
    id: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.put<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/restore`,
      {}
    );
  }

  deleteTenant(id: string): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/tenants/${id}`
    );
  }

  hardDeleteTenant(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/tenants/${id}/hard`
    );
  }

  bulkTenantOperation(
    tenantIds: string[],
    operation:
      | "suspend"
      | "activate"
      | "delete"
      | "restore"
      | "upgrade"
      | "downgrade",
    plan?: string
  ): Observable<{ success: boolean; data: any }> {
    return this.post<{ success: boolean; data: any }>("/tenants/bulk", {
      tenantIds,
      operation,
      plan,
    });
  }

  getTenantUsage(
    id: string
  ): Observable<{ success: boolean; data: TenantUsage }> {
    return this.get<{ success: boolean; data: TenantUsage }>(
      `/tenants/${id}/usage`
    );
  }

  getTenantBilling(
    id: string
  ): Observable<{ success: boolean; data: TenantBilling }> {
    return this.get<{ success: boolean; data: TenantBilling }>(
      `/tenants/${id}/billing`
    );
  }

  getTenantPlanFeatures(
    id: string
  ): Observable<{
    success: boolean;
    data: {
      plan: string;
      features: string[];
      menuItems: string[];
      wizardItems: string[];
      limits: any;
    };
  }> {
    return this.get<{
      success: boolean;
      data: {
        plan: string;
        features: string[];
        menuItems: string[];
        wizardItems: string[];
        limits: any;
      };
    }>(`/tenants/${id}/plan-features`);
  }

  updateTenantBilling(
    id: string,
    billingData: {
      billingEmail?: string;
      billingAddress?: any;
      subscriptionId?: string;
      subscriptionStatus?: string;
    }
  ): Observable<{ success: boolean; data: TenantBilling }> {
    return this.patch<{ success: boolean; data: TenantBilling }>(
      `/tenants/${id}/billing`,
      billingData
    );
  }

  onboardTenant(
    id: string,
    data: { adminEmail: string; adminPassword: string; adminName?: string }
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      `/tenants/${id}/onboard`,
      data
    );
  }

  offboardTenant(
    id: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.post<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/offboard`,
      {}
    );
  }

  migrateTenantToSeparateDb(
    id: string,
    dbConnectionString: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.post<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/migrate-separate-db`,
      { dbConnectionString }
    );
  }

  rollbackTenantMigration(
    id: string
  ): Observable<{ success: boolean; data: Tenant; message?: string }> {
    return this.post<{ success: boolean; data: Tenant; message?: string }>(
      `/tenants/${id}/rollback-migration`,
      {}
    );
  }

  // ========== ANALYTICS ==========

  getCrossTenantAnalytics(): Observable<{
    success: boolean;
    data: PlatformAnalytics;
  }> {
    return this.get<{ success: boolean; data: PlatformAnalytics }>(
      "/analytics/cross-tenant"
    );
  }

  compareTenants(
    tenantIds: string[]
  ): Observable<{ success: boolean; data: any[] }> {
    const params = new HttpParams().set("tenantIds", tenantIds.join(","));
    return this.get<{ success: boolean; data: any[] }>("/analytics/compare", {
      params,
    });
  }

  getTenantGrowth(
    id: string,
    days?: number
  ): Observable<{ success: boolean; data: any }> {
    let httpParams = new HttpParams();
    if (days) httpParams = httpParams.set("days", days.toString());
    return this.get<{ success: boolean; data: any }>(
      `/analytics/tenant/${id}/growth`,
      { params: httpParams }
    );
  }

  getPlatformMetrics(
    timeframe?: "day" | "week" | "month"
  ): Observable<{ success: boolean; data: any }> {
    let httpParams = new HttpParams();
    if (timeframe) httpParams = httpParams.set("timeframe", timeframe);
    return this.get<{ success: boolean; data: any }>(
      "/analytics/platform-metrics",
      { params: httpParams }
    );
  }

  getRevenueAnalytics(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/analytics/revenue");
  }

  // ========== MONITORING ==========

  getResourceUsage(): Observable<{ success: boolean; data: ResourceUsage }> {
    return this.get<{ success: boolean; data: ResourceUsage }>(
      "/monitoring/resource-usage"
    );
  }

  getTenantHealth(id: string): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(
      `/monitoring/tenant/${id}/health`
    );
  }

  getSystemAlerts(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/monitoring/alerts");
  }

  getPerformanceMetrics(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/monitoring/performance");
  }

  // ========== SYSTEM ==========

  getSystemHealth(): Observable<{ success: boolean; data: SystemHealth }> {
    return this.get<{ success: boolean; data: SystemHealth }>("/system/health");
  }

  getSystemInfo(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/system/info");
  }

  getSystemConfig(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/system/config");
  }

  refreshStatistics(): Observable<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    return this.post<{ success: boolean; message?: string; data?: any }>(
      "/system/refresh-statistics",
      {}
    );
  }

  clearCache(): Observable<{ success: boolean; message?: string }> {
    return this.post<{ success: boolean; message?: string }>(
      "/system/clear-cache",
      {}
    );
  }

  backupDatabase(): Observable<{
    success: boolean;
    message?: string;
    backupId?: string;
  }> {
    return this.post<{ success: boolean; message?: string; backupId?: string }>(
      "/system/backup",
      {}
    );
  }

  getSystemLogs(limit?: number): Observable<{ success: boolean; data: any }> {
    let httpParams = new HttpParams();
    if (limit) httpParams = httpParams.set("limit", limit.toString());
    return this.get<{ success: boolean; data: any }>("/system/logs", {
      params: httpParams,
    });
  }

  exportTenants(
    tenantIds?: string[],
    format: "json" | "csv" = "json",
    includeData: boolean = false
  ): Observable<{ success: boolean; data: any }> {
    return this.post<{ success: boolean; data: any }>("/tenants/export", {
      tenantIds,
      format,
      includeData,
    });
  }

  importTenants(
    file: File,
    format: "json" | "csv" = "json"
  ): Observable<{ success: boolean; message?: string; data?: any }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);
    const headers = this.getHeaders().delete("Content-Type"); // Let browser set multipart/form-data
    const url = `${this.baseUrl}/tenants/import`;
    return this.http.post<{ success: boolean; message?: string; data?: any }>(
      url,
      formData,
      { headers }
    ) as Observable<{ success: boolean; message?: string; data?: any }>;
  }

  // Alias for getCrossTenantAnalytics to match dashboard expectations
  getAnalytics(): Observable<{ success: boolean; data: PlatformAnalytics }> {
    return this.getCrossTenantAnalytics().pipe(
      map((res) => ({
        ...res,
        data: {
          ...res.data,
          totalTenants: res.data.overview?.total_tenants || 0,
          activeTenants: res.data.overview?.active_tenants || 0,
          totalUsers: res.data.overview?.total_users || 0,
          totalTasks: res.data.overview?.total_tasks || 0,
          systemHealth: {
            cpu: 45, // Placeholder - would come from system metrics
            memory: 62,
            disk: 38,
          },
        },
      }))
    );
  }

  // ========== SETTINGS ==========

  getPlatformSettings(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>("/settings");
  }

  updatePlatformSettings(
    settings: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      "/settings",
      settings
    );
  }

  getEmailTemplates(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      "/settings/email-templates"
    );
  }

  createEmailTemplate(
    template: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/settings/email-templates",
      template
    );
  }

  updateEmailTemplate(
    id: string,
    template: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/settings/email-templates/${id}`,
      template
    );
  }

  deleteEmailTemplate(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/settings/email-templates/${id}`
    );
  }

  // ========== CONFIGURATION ==========

  getFeatureFlags(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      "/configuration/feature-flags"
    );
  }

  createFeatureFlag(
    flag: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/configuration/feature-flags",
      flag
    );
  }

  updateFeatureFlag(
    id: string,
    flag: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/configuration/feature-flags/${id}`,
      flag
    );
  }

  deleteFeatureFlag(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/configuration/feature-flags/${id}`
    );
  }

  getApiKeys(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      "/configuration/api-keys"
    );
  }

  createApiKey(
    key: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/configuration/api-keys",
      key
    );
  }

  revokeApiKey(id: string): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/configuration/api-keys/${id}`
    );
  }

  getWebhooks(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      "/configuration/webhooks"
    );
  }

  createWebhook(
    webhook: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/configuration/webhooks",
      webhook
    );
  }

  updateWebhook(
    id: string,
    webhook: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/configuration/webhooks/${id}`,
      webhook
    );
  }

  deleteWebhook(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/configuration/webhooks/${id}`
    );
  }

  testWebhook(
    id: string
  ): Observable<{ success: boolean; message?: string; data?: any }> {
    return this.post<{ success: boolean; message?: string; data?: any }>(
      `/configuration/webhooks/${id}/test`,
      {}
    );
  }

  getIntegrations(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      "/configuration/integrations"
    );
  }

  createIntegration(
    integration: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/configuration/integrations",
      integration
    );
  }

  updateIntegration(
    id: string,
    integration: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/configuration/integrations/${id}`,
      integration
    );
  }

  deleteIntegration(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/configuration/integrations/${id}`
    );
  }

  getSystemLimits(): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(
      "/configuration/system-limits"
    );
  }

  updateSystemLimits(
    limits: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      "/configuration/system-limits",
      limits
    );
  }

  // ========== BILLING ==========

  getSubscriptions(
    tenantId?: string
  ): Observable<{ success: boolean; data: any[] }> {
    const params = tenantId
      ? new HttpParams().set("tenantId", tenantId)
      : undefined;
    return this.get<{ success: boolean; data: any[] }>(
      "/billing/subscriptions",
      { params }
    );
  }

  getSubscription(id: string): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(
      `/billing/subscriptions/${id}`
    );
  }

  createSubscription(
    subscription: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/billing/subscriptions",
      subscription
    );
  }

  updateSubscription(
    id: string,
    subscription: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/billing/subscriptions/${id}`,
      subscription
    );
  }

  cancelSubscription(
    id: string
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      `/billing/subscriptions/${id}/cancel`,
      {}
    );
  }

  getInvoices(
    subscriptionId?: string
  ): Observable<{ success: boolean; data: any[] }> {
    const params = subscriptionId
      ? new HttpParams().set("subscriptionId", subscriptionId)
      : undefined;
    return this.get<{ success: boolean; data: any[] }>("/billing/invoices", {
      params,
    });
  }

  createInvoice(
    invoice: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/billing/invoices",
      invoice
    );
  }

  getPaymentMethods(
    tenantId: string
  ): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>(
      `/billing/payment-methods/${tenantId}`
    );
  }

  createPaymentMethod(
    method: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/billing/payment-methods",
      method
    );
  }

  getUsage(
    tenantId: string,
    metric?: string,
    startDate?: string,
    endDate?: string
  ): Observable<{ success: boolean; data: any[] }> {
    let httpParams = new HttpParams();
    if (metric) httpParams = httpParams.set("metric", metric);
    if (startDate) httpParams = httpParams.set("startDate", startDate);
    if (endDate) httpParams = httpParams.set("endDate", endDate);
    return this.get<{ success: boolean; data: any[] }>(
      `/billing/usage/${tenantId}`,
      { params: httpParams }
    );
  }

  trackUsage(
    usage: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/billing/usage",
      usage
    );
  }

  // ========== AUDIT (using dedicated audit service via gateway) ==========

  createAuditLog(log: any): Observable<{ success: boolean; data: any }> {
    // Call audit service directly via gateway
    const auditBaseUrl = `${API_CONFIG.getUrl(API_CONFIG.endpoints.gateway)}/audit`;
    return this.http
      .post<{ success: boolean; data: any }>(`${auditBaseUrl}/log`, log, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  queryAuditLogs(
    params?: any
  ): Observable<{
    success: boolean;
    data: any[];
    total?: number;
    pagination?: any;
  }> {
    // Call audit service directly via gateway
    const auditBaseUrl = `${API_CONFIG.getUrl(API_CONFIG.endpoints.gateway)}/audit`;
    const queryParams: Record<string, any> = {};
    if (params?.userId) queryParams.userId = params.userId;
    if (params?.service) queryParams.service = params.service;
    if (params?.action) queryParams.action = params.action;
    if (params?.resource) queryParams.resource = params.resource;
    if (params?.resourceId) queryParams.resourceId = params.resourceId;
    if (params?.status) queryParams.status = params.status;
    // Convert date-only strings to ISO 8601 format
    if (params?.startDate) {
      const date = new Date(params.startDate);
      queryParams.startDate = date.toISOString();
    }
    if (params?.endDate) {
      // Set end date to end of day (23:59:59.999)
      const date = new Date(params.endDate);
      date.setHours(23, 59, 59, 999);
      queryParams.endDate = date.toISOString();
    }
    if (params?.limit) queryParams.limit = params.limit.toString();
    if (params?.offset) queryParams.offset = params.offset.toString();
    // Convert page to offset if provided
    if (params?.page && params?.limit) {
      queryParams.offset = ((params.page - 1) * params.limit).toString();
    }

    const httpParams = this.buildParams(queryParams);
    return this.http
      .get<{
        success: boolean;
        data: any[];
        total?: number;
        limit?: number;
        offset?: number;
      }>(`${auditBaseUrl}/logs`, {
        headers: this.getHeaders(),
        params: httpParams,
      })
      .pipe(
        map((response) => ({
          ...response,
          pagination:
            response.total !== undefined
              ? {
                  page: params?.page || 1,
                  limit: response.limit || 50,
                  total: response.total,
                  totalPages: Math.ceil(
                    (response.total || 0) / (response.limit || 50)
                  ),
                }
              : undefined,
        })),
        catchError(this.handleError.bind(this))
      );
  }

  exportAuditLogs(
    params?: any,
    format: "json" | "csv" = "json"
  ): Observable<{ success: boolean; data: any; format: string }> {
    // Note: Audit service doesn't have export endpoint, so we'll get logs and format them
    return this.queryAuditLogs({ ...params, limit: 10000 }).pipe(
      map((response) => {
        if (format === "csv" && response.data) {
          const logs = response.data;
          if (logs.length === 0) {
            return { success: true, data: "", format: "csv" };
          }
          const headers = Object.keys(logs[0]).join(",");
          const rows = logs.map((log: any) => {
            return Object.values(log)
              .map((val: any) => {
                if (val === null || val === undefined) return "";
                if (typeof val === "object") return JSON.stringify(val);
                return String(val).replace(/"/g, '""');
              })
              .map((val: any) => `"${val}"`)
              .join(",");
          });
          const csv = [headers, ...rows].join("\n");
          return { success: true, data: csv, format: "csv" };
        }
        return { success: true, data: response.data, format: "json" };
      })
    );
  }

  getRetentionPolicy(): Observable<{ success: boolean; data: any }> {
    // Note: Audit service doesn't have retention policy endpoint yet, return statistics instead
    const auditBaseUrl = `${API_CONFIG.getUrl(API_CONFIG.endpoints.gateway)}/audit`;
    return this.http
      .get<{ success: boolean; data: any }>(`${auditBaseUrl}/statistics`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  applyRetentionPolicy(): Observable<{
    success: boolean;
    message?: string;
    deletedCount?: any;
  }> {
    // Note: Audit service doesn't have retention policy endpoint yet
    return this.http
      .post<{
        success: boolean;
        message?: string;
        deletedCount?: any;
      }>(`${API_CONFIG.getUrl(API_CONFIG.endpoints.gateway)}/audit/retention-policy/apply`, {}, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ========== BACKUP ==========

  getBackups(tenantId?: string): Observable<{ success: boolean; data: any[] }> {
    const params = tenantId
      ? new HttpParams().set("tenantId", tenantId)
      : undefined;
    return this.get<{ success: boolean; data: any[] }>("/backup", { params });
  }

  getBackup(id: string): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(`/backup/${id}`);
  }

  createBackup(
    backup: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/backup",
      backup
    );
  }

  restoreBackup(
    restore: any
  ): Observable<{ success: boolean; message?: string; data?: any }> {
    return this.post<{ success: boolean; message?: string; data?: any }>(
      "/backup/restore",
      restore
    );
  }

  verifyBackup(
    id: string
  ): Observable<{ success: boolean; message?: string; data?: any }> {
    return this.post<{ success: boolean; message?: string; data?: any }>(
      `/backup/${id}/verify`,
      {}
    );
  }

  deleteBackup(id: string): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(`/backup/${id}`);
  }

  getBackupSchedules(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>("/backup/schedules");
  }

  createBackupSchedule(
    schedule: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/backup/schedules",
      schedule
    );
  }

  updateBackupSchedule(
    id: string,
    schedule: any
  ): Observable<{ success: boolean; message?: string }> {
    return this.patch<{ success: boolean; message?: string }>(
      `/backup/schedules/${id}`,
      schedule
    );
  }

  deleteBackupSchedule(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/backup/schedules/${id}`
    );
  }

  // ========== BRANDING ==========

  getTenantBranding(
    tenantId: string
  ): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(
      `/branding/tenant/${tenantId}`
    );
  }

  updateTenantBranding(
    tenantId: string,
    branding: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/branding/tenant/${tenantId}`,
      branding
    );
  }

  getAllTenantBranding(): Observable<{ success: boolean; data: any[] }> {
    return this.get<{ success: boolean; data: any[] }>("/branding/all");
  }

  resetTenantBranding(
    tenantId: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/branding/tenant/${tenantId}`
    );
  }

  // ========== BACKGROUNDS ==========

  getBackgrounds(
    category?: string
  ): Observable<{ success: boolean; data: any[] }> {
    const params = category
      ? new HttpParams().set("category", category)
      : undefined;
    return this.get<{ success: boolean; data: any[] }>("/backgrounds", {
      params,
    });
  }

  // getDefaultBackground(): Observable<{ success: boolean; data: any }> {
  //   // GET /backgrounds/default - Fetch platform-wide default background image
  //   return this.get<{ success: boolean; data: any }>("/backgrounds/default");
  // }

  getBackground(id: string): Observable<{ success: boolean; data: any }> {
    return this.get<{ success: boolean; data: any }>(`/backgrounds/${id}`);
  }

  createBackground(
    background: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.post<{ success: boolean; data: any; message?: string }>(
      "/backgrounds",
      background
    );
  }

  updateBackground(
    id: string,
    background: any
  ): Observable<{ success: boolean; data: any; message?: string }> {
    return this.patch<{ success: boolean; data: any; message?: string }>(
      `/backgrounds/${id}`,
      background
    );
  }

  deleteBackground(
    id: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(
      `/backgrounds/${id}`
    );
  }

  assignBackground(
    assignment: any
  ): Observable<{ success: boolean; message?: string }> {
    return this.post<{ success: boolean; message?: string }>(
      "/backgrounds/assign",
      assignment
    );
  }
}
